// AnnouncerBanner — big, center-screen, single-line callouts for
// high-impact match moments. Distinct from CriticalFlipBanner (which is
// a small top-of-screen stack for tactical disruption events): this is
// the "Halo announcer" — one beat at a time, maximum stage presence.
//
// Variants:
//   - first_blood      red glow,        "SIGNAL SEVERED!"
//   - double / triple  gold escalation, "DUAL ERASURE!" / "TRIPLE DELETION!"
//   - rampage          orange,          "SYSTEM BREACH!"
//   - godlike          purple,          "SINGULARITY!"
//   - countdown_3/2/1  cyan,            "3" "2" "1"
//   - fight            yellow burst,    "SIGNAL LIVE"
//   - victory / defeat large summary    (end of match)
//   - overtime         purple shimmer,  "OVERTIME!"
//   - signal_storm     pink/cyan,       "SIGNAL STORM!"
//   - signal_surge     green pulse,     "SIGNAL SURGE!"
//
// Pure presentation (Rule 1). DOM-based; sits above the Phaser canvas
// at z-index 35 so it overlays the critical-flip stack (z-index 26).
// Queue-based: each push appends to a queue and the next slot plays
// only when the current one finishes, so a rampage doesn't overwrite
// double-kill mid-fade. The match-result tier (victory / defeat) sits
// at z-index 60 so it floats ABOVE the End-of-Match modal (z-index 50)
// — the V/D burst is the climactic frame the EOM stats summary then
// fades up under.

const ROOT_ID = 'announcer-banner';
const STYLE_ID = 'announcer-banner-styles';

export type AnnouncerVariant =
  | 'first_blood'
  | 'double'
  | 'triple'
  | 'rampage'
  | 'godlike'
  | 'countdown'
  | 'fight'
  | 'victory'
  | 'defeat'
  | 'overtime'
  | 'signal_storm'
  | 'signal_surge'
  // V2 Tempo near-event telegraphs — fire once per match on rising-edge
  // HP thresholds. The goal is anticipation: a player who sees "ENEMY
  // CORE EXPOSED!" pulls forward and pushes, a player who sees "CORE
  // UNDER FIRE!" panics back. Both increase emotional stakes mid-match.
  | 'enemy_core_exposed'
  | 'core_under_fire'
  // V2 Tempo — Underdog Surge engagement spectacle. Fires once per
  // match when the trailing-player surge first engages (income boost
  // applied). Tells the player "the game is helping you push back" so
  // the comeback feels earned + framed.
  | 'comeback_initiated';

interface VariantSpec {
  label: string;
  className: string;
  // Display duration (ms). Countdown digits are shorter than streak banners
  // so the 3-2-1 sequence reads as a clock, not a slideshow.
  durationMs: number;
}

const SPECS: Record<AnnouncerVariant, VariantSpec> = {
  first_blood: { label: 'SIGNAL SEVERED!', className: 'announcer-banner--first-blood', durationMs: 1800 },
  double: { label: 'DUAL ERASURE!', className: 'announcer-banner--double', durationMs: 1500 },
  triple: { label: 'TRIPLE DELETION!', className: 'announcer-banner--triple', durationMs: 1700 },
  rampage: { label: 'SYSTEM BREACH!', className: 'announcer-banner--rampage', durationMs: 2000 },
  godlike: { label: 'SINGULARITY!', className: 'announcer-banner--godlike', durationMs: 2400 },
  countdown: { label: '', className: 'announcer-banner--countdown', durationMs: 900 },
  fight: { label: 'SIGNAL LIVE', className: 'announcer-banner--fight', durationMs: 1200 },
  // Match-result tier: long stage time, sits ABOVE the EOM modal (the
  // root toggles a top-tier class so z-index lifts to 60).
  victory: { label: 'VICTORY', className: 'announcer-banner--victory', durationMs: 2600 },
  defeat: { label: 'DEFEAT', className: 'announcer-banner--defeat', durationMs: 2600 },
  // Mid-match stage events. Overtime and the late-game Signal Storm
  // change the rules of the match — they deserve announcer presence,
  // not just a small top-of-screen flip banner.
  overtime: { label: 'OVERTIME!', className: 'announcer-banner--overtime', durationMs: 2200 },
  signal_storm: { label: 'SIGNAL STORM!', className: 'announcer-banner--signal-storm', durationMs: 2200 },
  signal_surge: { label: 'SIGNAL SURGE!', className: 'announcer-banner--signal-surge', durationMs: 1800 },
  enemy_core_exposed: { label: 'ENEMY CORE EXPOSED!', className: 'announcer-banner--enemy-core-exposed', durationMs: 1800 },
  core_under_fire: { label: 'CORE UNDER FIRE!', className: 'announcer-banner--core-under-fire', durationMs: 1800 },
  comeback_initiated: { label: 'COMEBACK INITIATED!', className: 'announcer-banner--comeback-initiated', durationMs: 2000 },
};

interface QueueEntry {
  variant: AnnouncerVariant;
  // Override label (used by 'countdown' to show "3" / "2" / "1").
  labelOverride: string | undefined;
}

export class AnnouncerBanner {
  private root: HTMLDivElement | null = null;
  private currentEl: HTMLDivElement | null = null;
  private currentTimer: ReturnType<typeof setTimeout> | null = null;
  private queue: QueueEntry[] = [];

  constructor() {
    injectStyles();
    this.root = ensureRoot();
  }

  push(variant: AnnouncerVariant, labelOverride?: string): void {
    this.queue.push({ variant, labelOverride });
    this.advance();
  }

  // Convenience helpers for the common entry points.
  pushFirstBlood(): void {
    this.push('first_blood');
  }

  pushStreak(tier: 'double' | 'triple' | 'rampage' | 'godlike'): void {
    this.push(tier);
  }

  // Match-result tier sits ABOVE the EOM modal. We lift the root's
  // z-index for the lifetime of this banner so the V/D burst reads as
  // the climactic frame the EOM stats card fades up under.
  pushMatchResult(youWon: boolean): void {
    if (this.root !== null) this.root.classList.add('announcer-banner-root--top');
    this.push(youWon ? 'victory' : 'defeat');
  }

  pushOvertime(): void {
    this.push('overtime');
  }

  pushSignalStorm(): void {
    this.push('signal_storm');
  }

  pushEnemyCoreExposed(): void {
    this.push('enemy_core_exposed');
  }

  pushCoreUnderFire(): void {
    this.push('core_under_fire');
  }

  pushComebackInitiated(): void {
    this.push('comeback_initiated');
  }

  pushSignalSurge(): void {
    this.push('signal_surge');
  }

  // Drive the "3 ... 2 ... 1 ... FIGHT!" sequence as four scheduled
  // pushes. Each call sets its own timer so they fire even if a banner
  // is still on screen (the queue absorbs the order). `onFightStart`
  // fires the instant the FIGHT banner is pushed (t=2700ms) — callers
  // use it to gate gameplay (input + sim) so the world is genuinely
  // frozen during the 3-2-1 count. Returns the total duration so the
  // caller can know when it's safe to assume the match-start spectacle
  // is done.
  scheduleMatchStartCountdown(onFightStart?: () => void): number {
    // Three digits, then FIGHT. Spacing matches the per-digit display
    // time so they read as a real clock cadence.
    setTimeout(() => this.push('countdown', '3'), 0);
    setTimeout(() => this.push('countdown', '2'), 900);
    setTimeout(() => this.push('countdown', '1'), 1800);
    setTimeout(() => {
      this.push('fight');
      onFightStart?.();
    }, 2700);
    return 2700 + SPECS.fight.durationMs;
  }

  clear(): void {
    this.queue.length = 0;
    if (this.currentTimer !== null) {
      clearTimeout(this.currentTimer);
      this.currentTimer = null;
    }
    if (this.currentEl !== null) {
      this.currentEl.remove();
      this.currentEl = null;
    }
    if (this.root !== null) this.root.classList.remove('announcer-banner-root--top');
  }

  dispose(): void {
    this.clear();
    if (this.root !== null) this.root.remove();
    this.root = null;
  }

  private advance(): void {
    if (this.currentEl !== null) return; // current is still on screen; will chain
    const next = this.queue.shift();
    if (next === undefined) return;
    this.show(next);
  }

  private show(entry: QueueEntry): void {
    if (this.root === null) return;
    const spec = SPECS[entry.variant];
    const el = document.createElement('div');
    el.className = `announcer-banner ${spec.className}`;
    const labelEl = document.createElement('span');
    labelEl.className = 'announcer-banner__label';
    labelEl.textContent = entry.labelOverride ?? spec.label;
    el.appendChild(labelEl);
    this.root.appendChild(el);
    this.currentEl = el;
    this.currentTimer = setTimeout(() => {
      el.classList.add('announcer-banner--out');
      setTimeout(() => {
        el.remove();
        this.currentEl = null;
        this.currentTimer = null;
        this.advance();
      }, 220);
    }, spec.durationMs);
  }
}

function ensureRoot(): HTMLDivElement {
  const existing = document.getElementById(ROOT_ID);
  if (existing instanceof HTMLDivElement) return existing;
  const div = document.createElement('div');
  div.id = ROOT_ID;
  document.body.appendChild(div);
  return div;
}

function injectStyles(): void {
  if (document.getElementById(STYLE_ID) !== null) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
#${ROOT_ID} {
  position: fixed;
  inset: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  pointer-events: none;
  z-index: 35;
  font-family: 'Orbitron', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
}
/* Match-result tier (VICTORY / DEFEAT) lifts the root above the EOM
 * modal (z-index 50) so it reads as the climactic frame the modal
 * fades up under. Removed in clear() on next-match teardown. */
#${ROOT_ID}.announcer-banner-root--top { z-index: 60; }
.announcer-banner {
  position: absolute;
  top: 28%;
  padding: 14px 36px;
  font-size: 56px;
  font-weight: 900;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  text-shadow: 0 0 18px currentColor, 0 0 38px currentColor, 0 2px 0 rgba(0,0,0,0.55);
  animation: announcer-banner-in 280ms cubic-bezier(0.16, 0.84, 0.44, 1.0);
  white-space: nowrap;
  user-select: none;
}
.announcer-banner--out {
  animation: announcer-banner-out 220ms ease-in forwards;
}
.announcer-banner__label { color: inherit; }
.announcer-banner--first-blood { color: #ff5a4a; }
.announcer-banner--double      { color: #ffd964; }
.announcer-banner--triple      { color: #ffae3e; font-size: 64px; }
.announcer-banner--rampage     { color: #ff7a2c; font-size: 72px; letter-spacing: 0.22em; }
.announcer-banner--godlike     { color: #c66bff; font-size: 80px; letter-spacing: 0.26em; }
.announcer-banner--countdown   { color: #6fd4ff; font-size: 140px; letter-spacing: 0; }
.announcer-banner--fight       { color: #f5e76e; font-size: 96px; letter-spacing: 0.32em; }
/* Match-result tier: largest stage-presence. Victory uses a warm
 * gold pulse; defeat is muted red — both intentionally heavier than
 * GODLIKE so the climactic frame outweighs any in-match streak. */
.announcer-banner--victory     { color: #ffd964; font-size: 112px; letter-spacing: 0.30em; animation: announcer-banner-in 320ms cubic-bezier(0.16, 0.84, 0.44, 1.0), announcer-banner-pulse 1.6s ease-in-out 320ms infinite alternate; }
.announcer-banner--defeat      { color: #ff5a4a; font-size: 100px; letter-spacing: 0.28em; }
/* Stage-event tier: overtime / signal storm / signal surge. Each
 * tinted to match its SFX colour identity (purple / pink / green). */
.announcer-banner--overtime     { color: #c66bff; font-size: 84px; letter-spacing: 0.22em; }
.announcer-banner--signal-storm { color: #ff6fc1; font-size: 78px; letter-spacing: 0.22em; }
.announcer-banner--signal-surge { color: #6fff9a; font-size: 76px; letter-spacing: 0.22em; }
.announcer-banner--enemy-core-exposed { color: #ff7a2c; font-size: 72px; letter-spacing: 0.22em; }
.announcer-banner--core-under-fire { color: #ff5a4a; font-size: 72px; letter-spacing: 0.22em; }
.announcer-banner--comeback-initiated { color: #6ed788; font-size: 76px; letter-spacing: 0.22em; text-shadow: 0 0 24px rgba(110,215,136,0.45); }
@keyframes announcer-banner-in {
  0%   { transform: scale(0.55); opacity: 0; }
  60%  { transform: scale(1.12); opacity: 1; }
  100% { transform: scale(1.0);  opacity: 1; }
}
@keyframes announcer-banner-out {
  0%   { transform: scale(1.0); opacity: 1; }
  100% { transform: scale(1.18); opacity: 0; }
}
@keyframes announcer-banner-pulse {
  0%   { transform: scale(1.00); filter: brightness(1.0); }
  100% { transform: scale(1.06); filter: brightness(1.15); }
}
@media (max-width: 820px), (pointer: coarse) {
  .announcer-banner            { font-size: 36px; padding: 10px 22px; }
  .announcer-banner--triple    { font-size: 42px; }
  .announcer-banner--rampage   { font-size: 48px; }
  .announcer-banner--godlike   { font-size: 54px; }
  .announcer-banner--countdown { font-size: 90px; }
  .announcer-banner--fight     { font-size: 60px; }
  .announcer-banner--victory   { font-size: 64px; }
  .announcer-banner--defeat    { font-size: 58px; }
  .announcer-banner--overtime,
  .announcer-banner--signal-storm,
  .announcer-banner--signal-surge,
  .announcer-banner--enemy-core-exposed,
  .announcer-banner--core-under-fire,
  .announcer-banner--comeback-initiated { font-size: 44px; }
}
`;
  document.head.appendChild(style);
}
